import { useState } from "react";
import FixaTable, {
  FixaTableBody,
  FixaTableHeader,
} from "@coworker/apprestructured/src/shared/wrappers/FixaTable/FixaTable";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import TrashCanIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/TrashCanIconPath";
import { FixaSwitch } from "@coworker/apprestructured/src/shared/wrappers/FixaSwitch/FixaSwitch";
import { CountryPermission } from "../../types/CountryPermission";

type SortColumn = "id" | "country_id" | "permission_id" | "value";
type SortDirection = "asc" | "desc";

type Props = {
  permissions: CountryPermission[];
  isLoading: boolean;
  onToggle: (permissionId: number, currentValue: boolean) => void;
  onDelete: (permissionId: number) => void;
};

const CountryPermissionsPanel = ({
  permissions,
  isLoading,
  onToggle,
  onDelete,
}: Props) => {
  const [sortColumn, setSortColumn] = useState<SortColumn>("country_id");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const handleHeaderClick = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortIndicator = (column: SortColumn) => {
    if (sortColumn !== column) return null;
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  const sortedPermissions = [...permissions].sort(
    (a: CountryPermission, b: CountryPermission) => {
      let aVal: number | string | boolean = a[sortColumn];
      let bVal: number | string | boolean = b[sortColumn];

      if (typeof aVal === "boolean" && typeof bVal === "boolean") {
        aVal = aVal ? 1 : 0;
        bVal = bVal ? 1 : 0;
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    }
  );

  return (
    <FixaTable fullWidth>
      <FixaTableHeader>
        <tr>
          <th
            style={{ cursor: "pointer" }}
            onClick={() => handleHeaderClick("id")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              (e.key === "Enter" || e.key === " ") && handleHeaderClick("id")
            }
            aria-sort={
              sortColumn === "id"
                ? sortDirection === "asc"
                  ? "ascending"
                  : "descending"
                : "none"
            }
          >
            ID{sortIndicator("id")}
          </th>
          <th
            style={{ cursor: "pointer" }}
            onClick={() => handleHeaderClick("country_id")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              (e.key === "Enter" || e.key === " ") &&
              handleHeaderClick("country_id")
            }
            aria-sort={
              sortColumn === "country_id"
                ? sortDirection === "asc"
                  ? "ascending"
                  : "descending"
                : "none"
            }
          >
            Country ID{sortIndicator("country_id")}
          </th>
          <th
            style={{ cursor: "pointer" }}
            onClick={() => handleHeaderClick("permission_id")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              (e.key === "Enter" || e.key === " ") &&
              handleHeaderClick("permission_id")
            }
            aria-sort={
              sortColumn === "permission_id"
                ? sortDirection === "asc"
                  ? "ascending"
                  : "descending"
                : "none"
            }
          >
            Permission Code{sortIndicator("permission_id")}
          </th>
          <th
            style={{ cursor: "pointer" }}
            onClick={() => handleHeaderClick("value")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              (e.key === "Enter" || e.key === " ") && handleHeaderClick("value")
            }
            aria-sort={
              sortColumn === "value"
                ? sortDirection === "asc"
                  ? "ascending"
                  : "descending"
                : "none"
            }
          >
            Value{sortIndicator("value")}
          </th>
          <th>Delete</th>
        </tr>
      </FixaTableHeader>
      <FixaTableBody>
        {isLoading ? (
          <tr>
            <td colSpan={5}>Loading...</td>
          </tr>
        ) : permissions.length === 0 ? (
          <tr>
            <td colSpan={5}>No country permissions found</td>
          </tr>
        ) : (
          sortedPermissions.map((permission: CountryPermission) => (
            <tr key={permission.id}>
              <td>{permission.id}</td>
              <td>{permission.country_id}</td>
              <td>{permission.permission_id}</td>
              <td>
                <FixaSwitch
                  checked={permission.value}
                  id={`country-permission-${permission.id}`}
                  value={permission.value.toString()}
                  onChange={() => onToggle(permission.id, permission.value)}
                />
              </td>
              <td>
                <FixaButton
                  iconOnly={true}
                  size="small"
                  ssrIcon={TrashCanIconPath}
                  onClick={() => onDelete(permission.id)}
                />
              </td>
            </tr>
          ))
        )}
      </FixaTableBody>
    </FixaTable>
  );
};

export default CountryPermissionsPanel;
